import type { AxiosProgressEvent } from "axios";
import { BASE_URL_MAP } from "~/core/constants/constants";
import type { ContentResource } from "~/core/models/ContentResource.interface";
import type { ContentResourceCreatePayload } from "~/core/models/ContentResourceCreatePayload.interface";
import { ContentTags } from "~/core/models/ContentTags";
import type LxcError from "~/core/models/LxcError.class";
import type { FiltersSelection } from "~/core/models/filters";
import RestService from "~/core/services/rest.service";
import type { BusinessUnit } from "~/modules/businessUnit/models/BusinessUnit.interface";
import type { BusinessUnitUpdate } from "~/modules/businessUnit/models/BusinessUnitUpdate.type";
import type { GlobalConfigurationAddUpdatePayload } from "~/modules/businessUnit/models/GlobalConfigurationAddUpdatePayload.interface";
import { GlobalConfigurationStatus } from "~/modules/businessUnit/models/GlobalConfigurationStatus";

export class BusinessUnitService extends RestService {
  async getBusinessUnits(
    filter?: string | FiltersSelection,
  ): Promise<BusinessUnit[] | LxcError> {
    let params = {};

    if (filter) {
      params = { ...params, filter };
    }

    return this.$apiV2.get(this.getBusinessUnitsPath(), { params });
  }

  updateBusinessUnit(businessUnit: BusinessUnitUpdate): Promise<BusinessUnit> {
    return this.$apiV2.put(
      this.getBusinessUnitPath(businessUnit.code),
      businessUnit,
    );
  }

  async uploadGlobalConfiguration(
    file: File,
    progress: Ref<number | undefined | null>,
    businessUnitCode?: number,
  ): Promise<ContentResource> {
    const formData = new FormData();
    formData.append("file0", file);

    const contentResourcePayload: ContentResourceCreatePayload = {
      name: file.name,
      temporary: true,
      files: [
        {
          name: file.name,
          compressedFile: true,
          fileValidatorRule: [
            {
              fileNameRegex: ".*\\.json",
              validator: "global-configuration-manifest",
            },
          ],
          visible: true,
        },
      ],
      descriptor: {
        status: GlobalConfigurationStatus.INACTIVE,
      },
      tags: [ContentTags.CONFIGURATION, ContentTags.GLOBAL, ContentTags.DEVICE],
    };

    const contentResourceBlob = new Blob(
      [JSON.stringify(contentResourcePayload)],
      { type: "application/json" },
    );
    formData.append("contentResource", contentResourceBlob);

    const config = {
      headers: {
        "Content-Type": "multipart/form-data",
      },
      onUploadProgress: (progressEvent: AxiosProgressEvent) => {
        progress.value = progressEvent.total
          ? (progressEvent.loaded / progressEvent.total) * 100
          : 0;
      },
      timeout: Infinity,
    };

    return this.$apiV4.post(
      `${this.getBusinessUnitContentsPath(businessUnitCode)}`,
      formData,
      config,
    );
  }

  /**
   * Add or update configuration files
   * Note: This service allows persisting the contentm ressource (temporary=false).
   * All the attributes of the ressources cannot be updated.
   * @param contentResourceUid
   * @param descriptor
   * @param businessUnitCode
   */
  async updateGlobalConfiguration(
    contentResourceUid: string,
    descriptor: Record<string, unknown>,
    businessUnitCode?: number,
  ): Promise<void> {
    const patchGlobalConfigurationPayload: GlobalConfigurationAddUpdatePayload =
      {
        descriptor,
        temporary: false,
      };

    return this.$apiV4.patch(
      `${this.getBusinessUnitContentsPath(businessUnitCode)}/${contentResourceUid}`,
      patchGlobalConfigurationPayload,
    );
  }

  /**
   * Delete configuration files
   * @param contentResourceUid
   * @param businessUnitCode
   */
  async deleteGlobalConfiguration(
    contentResourceUid?: string,
    businessUnitCode?: number,
  ): Promise<void> {
    return this.$apiV4.delete(
      `${this.getBusinessUnitContentsPath(businessUnitCode)}/${contentResourceUid}`,
    );
  }

  getBusinessUnit(businessUnitCode: number): Promise<BusinessUnit> {
    return this.$apiV2.get(this.getBusinessUnitPath(businessUnitCode));
  }

  getBusinessUnitsPath(): string {
    return BASE_URL_MAP.BUSINESS_UNITS;
  }

  getBusinessUnitPath(businessUnitCode?: number): string {
    if (!businessUnitCode) {
      throw new Error("Business code is undefined");
    }
    return `${this.getBusinessUnitsPath()}/${businessUnitCode}`;
  }

  getBusinessUnitContentsPath(businessUnitCode?: number): string {
    return `${this.getBusinessUnitPath(businessUnitCode)}/contents`;
  }
}
export default new BusinessUnitService();
